import React, { useCallback, useEffect, useState } from "react";
import { Box, Stack, Typography, CircularProgress, Alert } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ShieldCheck, Trash2, Unlink, BarChart3 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogActions,
} from "../../../components/Dialog";
import { AlertNote } from "../../../components/AlertNote";
import { Button } from "../../../components/Button";
import { ConfirmModal } from "../../../components/ConfirmModal";
import { CustomSelector } from "../../../components/CustomSelector";
import {
  deleteGoogleAccount,
  linkAnalyticsProperty,
  listAnalyticsProperties,
  listGoogleAccounts,
  startGoogleAuthorization,
  unlinkAnalyticsProperty,
} from "../../../services/integrationsService";
import { useGoogleOAuthPopup } from "../../../hooks/useGoogleOAuthPopup";
import { googleReauthMessage, isGoogleReauthError } from "./googleReauth";
import { ConnectingClientField } from "./ConnectingClientField";

const errorText = (err, fallback) => {
  if (isGoogleReauthError(err)) return googleReauthMessage(err);
  // A 502 here wraps a raw Google API error (e.g. "insufficient authentication
  // scopes") that isn't safe or useful to show verbatim, so it's always
  // replaced with a friendly message rather than surfaced to the user.
  if (err?.response?.status === 502) {
    return "Algo ha salido mal. Vuelve a intentarlo en unos minutos si el problema persiste, contacta con soporte.";
  }
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  return fallback;
};

export function ConnectGoogleAnalyticsModal({
  open,
  onOpenChange,
  clientId,
  client,
  currentLink,
  onConnected,
  onDisconnected,
}) {
  const theme = useTheme();

  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [gaAccounts, setGaAccounts] = useState([]);
  const [gaAccountId, setGaAccountId] = useState("");
  const [propertyId, setPropertyId] = useState("");

  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [isLoadingProperties, setIsLoadingProperties] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  // Which destructive action is waiting for confirmation: "account" or "unlink".
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const [error, setError] = useState("");
  // Set after a successful link whose property tracks another website. The
  // link is already saved; the dialog stays open only to show the warning.
  const [domainMismatch, setDomainMismatch] = useState(null);

  const loadAccounts = useCallback(
    async (preferredId) => {
      setIsLoadingAccounts(true);
      setError("");
      try {
        const data = await listGoogleAccounts();
        setAccounts(data);

        // Prefer the account just authorized, then the one already linked, then
        // the only one available.
        const preferred =
          data.find((a) => preferredId && (a.id === preferredId || a.email === preferredId)) ||
          data.find((a) => a.id === currentLink?.google_account) ||
          (data.length === 1 ? data[0] : null);
        setAccountId(preferred ? String(preferred.id) : "");
      } catch (err) {
        setError(errorText(err, "No se han podido cargar las cuentas de Google."));
      } finally {
        setIsLoadingAccounts(false);
      }
    },
    [currentLink]
  );

  useEffect(() => {
    if (open) loadAccounts();
  }, [open, loadAccounts]);

  // Only a fresh opening clears the warning: connecting updates currentLink,
  // which reloads the accounts, and that must not wipe what was just shown.
  useEffect(() => {
    if (open) setDomainMismatch(null);
  }, [open]);

  const selectedAccount = accounts.find((a) => String(a.id) === String(accountId));
  // A dead account would only fail at Google; offer re-authorization instead.
  const needsReauth = selectedAccount?.is_active === false;

  // Loading properties is a live call to Google, so it only runs on demand.
  useEffect(() => {
    if (!accountId || needsReauth) {
      setGaAccounts([]);
      setGaAccountId("");
      setPropertyId("");
      return;
    }

    let cancelled = false;
    setIsLoadingProperties(true);
    setError("");

    listAnalyticsProperties(accountId)
      .then((data) => {
        if (cancelled) return;
        setGaAccounts(data);

        const preselected =
          data.find((a) => a.id === currentLink?.ga_account_id) ||
          (data.length === 1 ? data[0] : null);
        setGaAccountId(preselected ? preselected.id : "");
        setPropertyId(
          preselected?.properties?.some((p) => p.id === currentLink?.property_id)
            ? currentLink.property_id
            : ""
        );
      })
      .catch((err) => {
        if (cancelled) return;
        if (isGoogleReauthError(err)) {
          // The backend just flagged it; reflect that so the reauth prompt shows.
          setAccounts((prev) =>
            prev.map((a) =>
              String(a.id) === String(accountId) ? { ...a, is_active: false } : a
            )
          );
          return;
        }
        setError(
          errorText(
            err,
            "No se han podido leer las propiedades de esa cuenta de Google."
          )
        );
      })
      .finally(() => {
        if (!cancelled) setIsLoadingProperties(false);
      });

    return () => {
      cancelled = true;
    };
  }, [accountId, currentLink, needsReauth]);

  /**
   * Send the consultant to Google in a popup.
   *
   * A popup keeps the dialog and its half-made selection alive; a full redirect
   * would throw both away.
   */
  const handleAddAccount = async () => {
    setIsAuthorizing(true);
    setError("");
    try {
      const url = await startGoogleAuthorization();
      if (!openPopup(url)) {
        setError(
          "El navegador ha bloqueado la ventana de Google. Permite las ventanas emergentes y vuelve a intentarlo."
        );
        setIsAuthorizing(false);
        return;
      }
    } catch (err) {
      setError(errorText(err, "No se ha podido iniciar la conexión con Google."));
      setIsAuthorizing(false);
    }
  };

  // The callback page reports back here rather than reloading the whole screen.
  const { openPopup } = useGoogleOAuthPopup({
    active: open,
    onResult: ({ connected, error: oauthError }) => {
      setIsAuthorizing(false);
      if (oauthError) {
        setError(
          "Google no ha completado la autorización. Vuelve a intentarlo desde «Añadir cuenta nueva»."
        );
        return;
      }
      loadAccounts(connected);
    },
    // The popup was closed (or left open) without a result. The account may
    // still have been connected, so refresh (keeping the current selection)
    // instead of reporting an error.
    onAbandoned: () => {
      setIsAuthorizing(false);
      loadAccounts(accountId ? Number(accountId) : undefined);
    },
  });

  const handleDeleteAccount = async () => {
    if (!accountId) return;

    setIsDeletingAccount(true);
    setError("");
    try {
      await deleteGoogleAccount(accountId);
      setPendingConfirm(null);
      setAccountId("");
      await loadAccounts();
    } catch (err) {
      setPendingConfirm(null);
      // 409 means clients still depend on it; the backend lists which ones.
      const clients = err?.response?.data?.clients;
      if (clients?.length) {
        setError(
          `Esa cuenta sigue vinculada a ${clients.join(", ")}. Desvincula esos clientes antes de desconectarla.`
        );
        return;
      }
      setError(errorText(err, "No se ha podido desconectar la cuenta."));
    } finally {
      setIsDeletingAccount(false);
    }
  };

  /**
   * Remove this client's link. Nothing is revoked at Google: the account stays
   * connected for the rest of the agency, and once no client uses it the trash
   * button next to it disconnects and revokes it.
   */
  const handleUnlink = async () => {
    if (!currentLink?.id) return;

    setIsUnlinking(true);
    setError("");
    try {
      await unlinkAnalyticsProperty(currentLink.id);
      setPendingConfirm(null);
      onDisconnected?.();
      onOpenChange(false);
    } catch (err) {
      setPendingConfirm(null);
      setError(errorText(err, "No se ha podido desvincular la propiedad."));
    } finally {
      setIsUnlinking(false);
    }
  };

  const handleConnect = async () => {
    const gaAccount = gaAccounts.find((a) => a.id === gaAccountId);
    const property = gaAccount?.properties.find((p) => p.id === propertyId);
    if (!gaAccount || !property) return;

    setIsSaving(true);
    setError("");
    try {
      const link = await linkAnalyticsProperty({
        client: clientId,
        google_account: Number(accountId),
        ga_account_id: gaAccount.id,
        ga_account_name: gaAccount.name,
        property_id: property.id,
        property_name: property.name,
      });
      onConnected?.(link);
      if (link?.domain_mismatch) {
        setDomainMismatch(link.domain_mismatch);
      } else {
        onOpenChange(false);
      }
    } catch (err) {
      setError(errorText(err, "No se ha podido guardar la conexión."));
    } finally {
      setIsSaving(false);
    }
  };

  const accountOptions = accounts.map((account) => ({
    value: String(account.id),
    label: account.is_active
      ? account.email
      : `${account.email} · requiere volver a autorizar`,
  }));

  const gaAccountOptions = gaAccounts.map((account) => ({
    value: account.id,
    label: account.name,
  }));

  const propertyOptions =
    gaAccounts
      .find((a) => a.id === gaAccountId)
      ?.properties.map((property) => ({
        value: property.id,
        label:
          property.type === "PROPERTY_TYPE_ORDINARY"
            ? `${property.name} (GA 4)`
            : property.name,
      })) || [];

  const canConnect = Boolean(accountId && gaAccountId && propertyId) && !isSaving;

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogHeader>
        <DialogTitle>Conectar Google Analytics</DialogTitle>
      </DialogHeader>

      <DialogContent>
        <Stack spacing={2.5} sx={{ width: { xs: "100%", sm: 380 }, paddingTop: 1 }}>
          {client && <ConnectingClientField client={client} />}

          {error && (
            <Alert severity="error" sx={{ wordBreak: "break-word" }}>
              {error}
            </Alert>
          )}

          {domainMismatch && (
            <AlertNote variant="warning">
              Propiedad vinculada, pero su web ({domainMismatch.property_host}) no
              coincide con la web del cliente ({domainMismatch.client_host}). Revisa que
              sea la propiedad correcta antes de fiarte de los datos.
            </AlertNote>
          )}

          <Box>
            <Typography variant="body2" sx={{ marginBottom: 1, fontWeight: 500 }}>
              Cuenta de Google:
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{ flex: 1 }}>
                <CustomSelector
                  searchable
                  value={accountId}
                  onChange={setAccountId}
                  options={accountOptions}
                  disabled={isLoadingAccounts}
                  placeholder={
                    isLoadingAccounts
                      ? "Cargando cuentas..."
                      : "Selecciona una cuenta"
                  }
                  leadingIcon={
                    <ShieldCheck size={16} color={theme.palette.success.main} />
                  }
                />
              </Box>
              <Button
                variant="outline"
                onClick={() => setPendingConfirm("account")}
                disabled={!accountId}
                sx={{ minWidth: 44, padding: "8px" }}
                aria-label="Desconectar cuenta de Google"
              >
                <Trash2 size={16} />
              </Button>
            </Stack>

            {needsReauth && (
              <AlertNote
                variant="warning"
                sx={{ marginTop: 1 }}
                action={
                  <Button
                    variant="outline"
                    onClick={handleAddAccount}
                    disabled={isAuthorizing}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    {isAuthorizing ? "Esperando..." : "Volver a autorizar"}
                  </Button>
                }
              >
                Google ha revocado el acceso de {selectedAccount.email}. Vuelve a
                autorizarla con esa misma cuenta de Google; los clientes vinculados
                se mantienen.
              </AlertNote>
            )}

            <Button
              variant="ghost"
              onClick={handleAddAccount}
              disabled={isAuthorizing}
              sx={{ paddingLeft: 0, marginTop: 0.5 }}
            >
              {isAuthorizing ? "Esperando a Google..." : "+ Añadir cuenta nueva"}
            </Button>
          </Box>

          <Box>
            <Typography variant="body2" sx={{ marginBottom: 1, fontWeight: 500 }}>
              Cuentas de Google Analytics:
            </Typography>
            <CustomSelector
              searchable
              value={gaAccountId}
              onChange={(value) => {
                setGaAccountId(value);
                setPropertyId("");
              }}
              options={gaAccountOptions}
              disabled={!accountId || isLoadingProperties}
              placeholder={
                isLoadingProperties
                  ? "Consultando Google..."
                  : "Selecciona una cuenta de Analytics"
              }
            />
          </Box>

          <Box>
            <Typography variant="body2" sx={{ marginBottom: 1, fontWeight: 500 }}>
              Propiedades y Apps:
            </Typography>
            <CustomSelector
              searchable
              value={propertyId}
              onChange={setPropertyId}
              options={propertyOptions}
              disabled={!gaAccountId}
              placeholder="Selecciona una propiedad"
              leadingIcon={<BarChart3 size={16} color={theme.palette.primary.main} />}
            />

            {currentLink?.id && (
              <Button
                variant="ghost"
                onClick={() => setPendingConfirm("unlink")}
                sx={{
                  paddingLeft: 0,
                  marginTop: 0.5,
                  color: theme.palette.error.main,
                  "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
                }}
              >
                <Unlink size={14} style={{ marginRight: 6 }} />
                Desvincular de este cliente
              </Button>
            )}
          </Box>

        </Stack>
      </DialogContent>

      <DialogActions>
        {domainMismatch ? (
          <Button onClick={() => onOpenChange(false)}>ENTENDIDO</Button>
        ) : (
          <>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              CANCELAR
            </Button>
            <Button onClick={handleConnect} disabled={!canConnect}>
              {isSaving ? <CircularProgress size={16} color="inherit" /> : "CONECTAR"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>

    <ConfirmModal
      open={pendingConfirm === "account"}
      onClose={() => setPendingConfirm(null)}
      onConfirm={handleDeleteAccount}
      title="Desconectar cuenta de Google"
      description={
        <>
          ¿Estás seguro de que quieres desconectar <strong>{selectedAccount?.email}</strong>? Dejará de leer datos de Analytics para toda la agencia y se revocará el acceso en Google.
        </>
      }
      confirmLabel="Desconectar"
      loadingLabel={<CircularProgress size={16} color="inherit" />}
      loading={isDeletingAccount}
      tone="warning"
    />

    <ConfirmModal
      open={pendingConfirm === "unlink"}
      onClose={() => setPendingConfirm(null)}
      onConfirm={handleUnlink}
      title="Desvincular Google Analytics"
      description={
        <>
          ¿Estás seguro de que quieres desvincular <strong>{currentLink?.property_name}</strong> de este cliente? Se dejarán de mostrar sus datos de Google Analytics. La cuenta de Google seguirá conectada para el resto de la agencia.
        </>
      }
      confirmLabel="Desvincular"
      loadingLabel={<CircularProgress size={16} color="inherit" />}
      loading={isUnlinking}
      tone="warning"
    />
    </>
  );
}

export default ConnectGoogleAnalyticsModal;
